/**
 * src/features/learning/components/SnapshotHistoryPanel.tsx
 *
 * MER Algo Footprints v7.1H — Snapshot History Panel
 *
 * Displays the decision snapshot history for the active symbol.
 * Clicking a row expands the full AIReviewPanel for that snapshot.
 *
 * Consumed by: Analytics71APanel — 📚 Review sub-tab
 */

import React, { useState, useEffect } from 'react';
import { useLearningData, LearningSnapshot, JournalInputs } from '../hooks/useLearningData';
import { AIReviewPanel } from './AIReviewPanel';

const mono: React.CSSProperties = { fontFamily: 'var(--mono)' };

const CLASSIFICATION_COLOR: Record<string, string> = {
  correct_thesis_correct_execution:  'var(--green)',
  correct_thesis_poor_execution:     'var(--cyan)',
  wrong_thesis_disciplined_pass:     'var(--cyan)',
  correct_avoid:                     'var(--cyan)',
  valid_setup_no_trigger:            'var(--blue-dim)',
  wrong_thesis_wrong_execution:      'var(--red)',
  false_positive:                    'var(--red)',
  rapid_invalidation:                'var(--amber)',
  missed_opportunity:                'var(--amber)',
};

const CLASSIFICATION_SHORT: Record<string, string> = {
  correct_thesis_correct_execution:  'W',
  correct_thesis_poor_execution:     'W~',
  wrong_thesis_disciplined_pass:     'P✓',
  correct_avoid:                     'A✓',
  valid_setup_no_trigger:            '—',
  wrong_thesis_wrong_execution:      'L',
  false_positive:                    'FP',
  rapid_invalidation:                '⚡',
  missed_opportunity:                '◈',
};

interface SnapshotRowProps {
  snap:      LearningSnapshot;
  expanded:  boolean;
  onToggle:  () => void;
  onReview:         (id: string) => Promise<any>;
  onAction:         (id: string, action: 'entered' | 'passed' | 'watching' | null) => Promise<boolean>;
  onJournalAccept:  (id: string, inputs: JournalInputs, draft: string) => Promise<boolean>;
  onJournalDiscard: (id: string) => Promise<boolean>;
  reviewLoading:    boolean;
  reviewError:      string | null;
}

function SnapshotRow({ snap, expanded, onToggle, onReview, onAction, onJournalAccept, onJournalDiscard, reviewLoading, reviewError }: SnapshotRowProps) {
  const cls      = snap.outcome_eval?.classification;
  const clsColor = cls ? (CLASSIFICATION_COLOR[cls] || 'var(--blue-dim)') : 'var(--border)';
  const clsShort = cls ? (CLASSIFICATION_SHORT[cls] || '?') : '○';
  const hasReview = !!snap.ai_review;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          borderBottom: '1px solid var(--border)',
          background: expanded ? 'var(--surface2)' : 'transparent',
        }}
      >
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: clsColor, width: 28, textAlign: 'center' }}>
          {clsShort}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 11, color: 'var(--gold)' }}>
          {snap.symbol}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: 'var(--cyan)' }}>
          {snap.setup_type?.replace(/_/g, ' ') || '—'}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10 }}>
          {snap.bias || '—'}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: snap.confidence != null ? 'var(--text)' : 'var(--blue-dim)' }}>
          {snap.confidence != null ? `${snap.confidence}` : '—'}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: 'var(--blue-dim)' }}>
          {new Date(snap.timestamp).toLocaleString('en-US', {
            month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
            timeZone: 'America/New_York',
          })}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: 'var(--blue-dim)' }}>
          {snap.user_action || '—'}
        </td>
        <td style={{ ...mono, padding: '5px 8px', fontSize: 10, color: hasReview ? 'var(--green)' : 'var(--border)' }}>
          {hasReview ? '✓' : '○'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} style={{ padding: '0 0 0 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <div style={{ padding: '12px 8px 12px 0' }}>
              <AIReviewPanel
                snapshot={snap}
                onTriggerReview={onReview}
                onUserAction={onAction}
                onJournalAccept={onJournalAccept}
                onJournalDiscard={onJournalDiscard}
                reviewLoading={reviewLoading}
                reviewError={reviewError}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface SnapshotHistoryPanelProps {
  symbol?: string;
}

export function SnapshotHistoryPanel({ symbol }: SnapshotHistoryPanelProps) {
  const {
    snapshots, snapshotsLoading, snapshotsError, loadSnapshots,
    triggerReview, reviewLoading, reviewError,
    recordUserAction,
    journalAccept, journalDiscard,
  } = useLearningData();

  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (symbol) loadSnapshots(symbol);
  }, [symbol, loadSnapshots]);

  const handleToggle = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  if (!symbol) {
    return (
      <div style={{ ...mono, fontSize: 11, color: 'var(--blue-dim)', padding: 16 }}>
        No symbol selected.
      </div>
    );
  }

  if (snapshotsLoading) {
    return (
      <div style={{ ...mono, fontSize: 11, color: 'var(--blue-dim)', padding: 16 }}>
        Loading snapshots for {symbol}…
      </div>
    );
  }

  if (snapshotsError) {
    return (
      <div style={{ ...mono, fontSize: 11, color: 'var(--red)', padding: 16 }}>
        Error: {snapshotsError}
      </div>
    );
  }

  if (!snapshots.length) {
    return (
      <div style={{ ...mono, fontSize: 11, color: 'var(--blue-dim)', padding: 16 }}>
        No snapshots yet for {symbol}. Decision snapshots are captured automatically when you generate a thesis.
      </div>
    );
  }

  return (
    <div>
      <div style={{ ...mono, fontSize: 9, color: 'var(--blue-dim)', padding: '8px 8px 4px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} · {symbol} · click row to expand
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Cls', 'Sym', 'Setup', 'Bias', 'Conf', 'Time', 'Action', 'Rev'].map(h => (
                <th key={h} style={{
                  ...mono, padding: '3px 8px', fontSize: 9, fontWeight: 400,
                  color: 'var(--blue-dim)', textAlign: 'left',
                  textTransform: 'uppercase', letterSpacing: '.5px',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {snapshots.map(snap => (
              <SnapshotRow
                key={snap.snapshot_id}
                snap={snap}
                expanded={expandedId === snap.snapshot_id}
                onToggle={() => handleToggle(snap.snapshot_id)}
                onReview={triggerReview}
                onAction={recordUserAction}
                onJournalAccept={journalAccept}
                onJournalDiscard={journalDiscard}
                reviewLoading={reviewLoading}
                reviewError={reviewError}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SnapshotHistoryPanel;
